import { createContext, useState, useEffect, useRef } from "react";
import { useAuth } from "../hooks";
import userImg from "../assets/users/admin.png";
import userImg2 from "../assets/users/operator.png";
import { userProviderPropTypes } from "../propTypes";

/**
 * UserContext - Provides user data and authentication state throughout the application
 * @type {React.Context}
 * @property {Object|null} userData - Current user data from API
 * @property {Function} setUserData - Updates user data state
 * @property {boolean} loading - Loading state during data fetch
 * @property {string} avatar - Selected avatar image path based on user role
 * @property {Function} refreshUserData - Manual refresh trigger for user data
 */
export const UserContext = createContext({
  userData: null,
  setUserData: () => {},
  loading: true,
  avatar: null,
  refreshUserData: () => {},
});

/**
 * UserProvider Component - Wraps application to provide user context and authentication state
 * Fetches user data on mount and provides mechanisms to refresh user information
 *
 * @component
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {JSX.Element} Provider component wrapping children
 */
export default function UserProvider({ children }) {
  const { getUserData } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  // Determines avatar based on user privileges
  const avatar = userData?.userPrivileges === "Admin" ? userImg : userImg2;

  // Fetches user data from authentication service with duplicate fetch prevention
  const fetchUser = async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    try {
      setLoading(true);
      const data = await getUserData();
      setUserData(data);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user data on component mount
  useEffect(() => {
    fetchUser();
  }, []);

  // Manually refresh user data - resets fetch guard and triggers new fetch
  const refreshUserData = () => {
    hasFetched.current = false;
    fetchUser();
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        loading,
        avatar,
        refreshUserData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

UserProvider.propTypes = userProviderPropTypes;
